import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "./index";

export type SignalRStatus = "Idle" | "Connecting" | "Connected" | "Failed" | "Disconnected";

export interface SignalRState {
  status: SignalRStatus;
  error: string | null;
  message: string | null;
  currentChannel: string | null;
  subscribedChannels: string[];
}

const initialState: SignalRState = {
  status: "Idle",
  error: null,
  message: null,
  currentChannel: null,
  subscribedChannels: [],
};

const signalRSlice = createSlice({
  name: "signalR",
  initialState,
  reducers: {
    setConnecting: (state) => {
      state.status = "Connecting";
      state.error = null;
    },
    setConnected: (state) => {
      state.status = "Connected";
      state.error = null;
    },
    setDisconnected: (state, action: PayloadAction<string | undefined>) => {
      state.status = "Disconnected";
      state.error = action.payload || null;
    },
    setFailed: (state, action: PayloadAction<string>) => {
      state.status = "Failed";
      state.error = action.payload;
    },
    receiveMessage: (state, action: PayloadAction<string>) => {
      state.message = action.payload;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    setCurrentChannel: (state, action: PayloadAction<string>) => {
      state.currentChannel = action.payload;
    },
    addSubscribedChannel: (state, action: PayloadAction<string>) => {
      if (!state.subscribedChannels.includes(action.payload)) {
        state.subscribedChannels.push(action.payload);
      }
    },
    removeSubscribedChannel: (state, action: PayloadAction<string>) => {
      state.subscribedChannels = state.subscribedChannels.filter(
        (ch) => ch !== action.payload
      );
    },
    clearSubscribedChannels: (state) => {
      state.subscribedChannels = [];
    },
    resetState: () => initialState,
  },
});

// Selectors
export const selectSignalRStatus = (state: RootState): SignalRStatus =>
  state.signalR.status;
export const selectSignalRError = (state: RootState): string | null =>
  state.signalR.error;
export const selectSignalRMessage = (state: RootState): string | null =>
  state.signalR.message;
export const selectCurrentChannel = (state: RootState): string | null =>
  state.signalR.currentChannel;
export const selectSubscribedChannels = (state: RootState): string[] =>
  state.signalR.subscribedChannels;
export const selectIsConnected = (state: RootState): boolean =>
  state.signalR.status === "Connected";

export const {
  setConnecting,
  setConnected,
  setDisconnected,
  setFailed,
  receiveMessage,
  clearMessage,
  setCurrentChannel,
  addSubscribedChannel,
  removeSubscribedChannel,
  clearSubscribedChannels,
  resetState,
} = signalRSlice.actions;

export const signalRReducer = signalRSlice.reducer;
export default signalRReducer;
